import { Box, Flex } from "@chakra-ui/react";
import MainContainer from "components/sections/MainContainer";
import MetaHead from "components/base/MetaHead";
import Text from "components/base/Text";
import Button from "components/base/Button";
import HomeHeroHeader3 from "components/sections/HomeHeroHeader3";
import HorizontalSlideSection from "components/sections/HorizontalSlideSection";
import DarkQuoteSection from "components/sections/DarkQuoteSection";
import FaqSection from "components/sections/FaqSection";
import PillsSection from "components/sections/PillsSection";
import CareerPathwaysSection from "components/sections/CareerPathwaysSection";
import TextColumnSection from "components/sections/TextColumnSection";
import BgChangeSection from "components/sections/BgChangeSection";
import { MotionBox, staggeredAppear, viewAppearDefault } from "utils/motion";
import { getFaqsByPage } from "lib/payloadApi";

const trans = {
  LBL_EARLY_CAREERS_META_TITLE: "Early Careers - Join Hill Helicopters",
  LBL_EARLY_CAREERS_META_DESCRIPTION:
    "Hill Helicopters are offering the opportunity to join their core team of highly skilled, dedicated designers and engineers and become part of a community for innovators who are on a mission to fully disrupt the modern general aviation market, representing the first real change in helicopter design in decades.",
  LBL_EARLY_CAREERS_HERO_SUP_TITLE: "Launch your",
  LBL_EARLY_CAREERS_HERO_TITLE: "Career",
  LBL_EARLY_CAREERS_HERO_SUBTITLE: "at Hill Helicopters",
  LBL_EARLY_CAREERS_HERO_CTA: "Apply now",
  LBL_EARLY_CAREERS_HERO_TAG_1: "Internships",
  LBL_EARLY_CAREERS_HERO_TAG_2: "Apprenticeships",
  LBL_EARLY_CAREERS_HERO_TAG_3: "Graduate Roles",
  LBL_EARLY_CAREERS_ABOUT_TITLE: "About Hill Helicopters",
  LBL_EARLY_CAREERS_ABOUT_TEXT:
    "Hill Helicopters is redefining rotorcraft. Based in Stafford, UK, we design, build, and support next-generation helicopters with a direct-to-customer model. Our mission is to deliver premium performance, unmatched safety, and accessibility in aviation — and we’re doing it with a world-class team. Whether you’re an engineer, technician, designer, or future leader, your career can take flight here.",
  LBL_EARLY_CAREERS_WHY_START_TITLE: "Why Start Your Career at Hill",
  LBL_EARLY_CAREERS_WHY_START_SUBTITLE:
    "Early career talent plays a real part in building aircraft and shaping the future of aviation.",
  LBL_EARLY_CAREERS_WHY_START_BULLET_1: "Hands-on work from day one",
  LBL_EARLY_CAREERS_WHY_START_BULLET_2:
    "Structured development with mentoring and coaching",
  LBL_EARLY_CAREERS_WHY_START_BULLET_3:
    "Cross-functional exposure across engineering, manufacturing, testing and support",
  LBL_EARLY_CAREERS_WHY_START_BULLET_5: "Agile teams and fast decision-making",
  LBL_EARLY_CAREERS_WHY_START_BULLET_6:
    "Clear progression opportunities alongside industry experts",
  LBL_EARLY_CAREERS_PATHWAYS_TITLE: "CAREER PATHWAYS",
  LBL_EARLY_CAREERS_PATHWAYS_TITLE_1: "Apprenticeships & Technician Training",
  LBL_EARLY_CAREERS_PATHWAYS_DESCRIPTION_1:
    "On-the-job training in manufacturing, composites, assembly, and quality — ideal for practical learners who want to build a long-term career in aviation.",
  LBL_EARLY_CAREERS_PATHWAYS_TITLE_2: "Internships & Placements",
  LBL_EARLY_CAREERS_PATHWAYS_DESCRIPTION_2:
    "6–12 month placements designed to give students hands-on experience, project ownership, and a taste of life in a pioneering aerospace business.",
  LBL_EARLY_CAREERS_PATHWAYS_TITLE_3: "Graduate Roles",
  LBL_EARLY_CAREERS_PATHWAYS_DESCRIPTION_3:
    "For recent graduates ready to apply their knowledge in real-world aerospace. Work alongside senior engineers on airframe design, flight testing, manufacturing, and more.",
  LBL_EARLY_CAREERS_OPPORTUNITIES_TITLE: "Where you could work",
  LBL_EARLY_CAREERS_OPPORTUNITIES_SUBTITLE:
    "WE OFFER OPPORTUNITIES ACROSS A VARIETY OF TEAMS",
  LBL_EARLY_CAREERS_QUOTE_TEXT:
    "At Hill Helicopters, we recognize the importance of investing in our future by fostering new talent through our Early Careers program. We are committed to providing hands-on experience with relevant qualifications, we cultivate a skilled workforce poised to grow with us and strengthen our position as a premier helicopter manufacturer.",
  LBL_EARLY_CAREERS_QUOTE_AUTHOR: "Mark Webb",
  LBL_EARLY_CAREERS_QUOTE_AUTHOR_TITLE: "Production Director",
  LBL_EARLY_CAREERS_OPPORTUNITIES_TEAM_1: "Airframe & Structures",
  LBL_EARLY_CAREERS_OPPORTUNITIES_TEAM_2: "Composites & Materials",
  LBL_EARLY_CAREERS_OPPORTUNITIES_TEAM_3: "Avionics & Electrical Systems",
  LBL_EARLY_CAREERS_OPPORTUNITIES_TEAM_4: "Certification & Flight Testing",
  LBL_EARLY_CAREERS_OPPORTUNITIES_TEAM_5: "Manufacturing & Assembly",
  LBL_EARLY_CAREERS_OPPORTUNITIES_TEAM_6: "Quality Assurance & Testing",
  LBL_EARLY_CAREERS_OPPORTUNITIES_TEAM_7: "Customer Support & Service",
  LBL_EARLY_CAREERS_FOOTER_CTA_TITLE: "What We are Looking For",
  LBL_EARLY_CAREERS_FOOTER_CTA_DESCRIPTION:
    "Strong technical or academic foundation (degree, HND, or equivalent), curiosity, problem-solving skills, and attention to detail, passion for aerospace, aviation, or advanced manufacturing, willingness to learn, collaborate, and get hands-on",
  LBL_EARLY_CAREERS_FORM_TITLE: "APPLY NOW",
  LBL_EARLY_CAREERS_FORM_CTA: "Apply Now",
};

const pathwayItems = [
  {
    title: trans.LBL_EARLY_CAREERS_PATHWAYS_TITLE_1,
    description: trans.LBL_EARLY_CAREERS_PATHWAYS_DESCRIPTION_1,
    image: "/images/early-careers/pathway-01.jpg",
  },
  {
    title: trans.LBL_EARLY_CAREERS_PATHWAYS_TITLE_2,
    description: trans.LBL_EARLY_CAREERS_PATHWAYS_DESCRIPTION_2,
    image: "/images/early-careers/pathway-02.jpg",
  },
  {
    title: trans.LBL_EARLY_CAREERS_PATHWAYS_TITLE_3,
    description: trans.LBL_EARLY_CAREERS_PATHWAYS_DESCRIPTION_3,
    image: "/images/early-careers/pathway-03.jpg",
  },
];

export default function EarlyCareers({ faqCategories, faqSchema }) {
  return (
    <MainContainer navStyle={{ gradient: false, inverted: false }} footerSmall>
      <MetaHead
        title={trans.LBL_EARLY_CAREERS_META_TITLE}
        description={trans.LBL_EARLY_CAREERS_META_DESCRIPTION}
        imageUrl="/images/meta/team.jpg"
        structuredData={faqSchema}
      />
      {/* Hero Section */}
      <HomeHeroHeader3
        animationId="header2b"
        image={{
          base: "/images/early-careers/header-mobile.jpg",
          sm: "/images/early-careers/header.jpg",
        }}
        supTitle={trans.LBL_EARLY_CAREERS_HERO_SUP_TITLE}
        title={trans.LBL_EARLY_CAREERS_HERO_TITLE}
        subtitle={trans.LBL_EARLY_CAREERS_HERO_SUBTITLE}
        cta={trans.LBL_EARLY_CAREERS_HERO_CTA}
        tags={[
          trans.LBL_EARLY_CAREERS_HERO_TAG_1,
          trans.LBL_EARLY_CAREERS_HERO_TAG_2,
          trans.LBL_EARLY_CAREERS_HERO_TAG_3,
        ]}
      />

      <Flex
        width="100%"
        minHeight={["50vh", null, "70vh"]}
        flexDirection="column"
        justifyContent="flex-start"
      >
        <Flex w="100%" h="100%" position="relative" justify="center">
          <Box align="center" pb="xl" mx={["md", null, "xxl"]}>
            <Text
              type="title_lg"
              color="primary"
              mb="md"
              variants={viewAppearDefault}
            >
              {trans.LBL_EARLY_CAREERS_ABOUT_TITLE}
            </Text>
            <Text
              type="p_lg"
              color="white"
              variants={viewAppearDefault}
              maxW="lg"
            >
              {trans.LBL_EARLY_CAREERS_ABOUT_TEXT}
            </Text>
          </Box>
        </Flex>
      </Flex>

      {/* Why Start Here Section */}
      <TextColumnSection
        title={trans.LBL_EARLY_CAREERS_WHY_START_TITLE}
        subtitle={trans.LBL_EARLY_CAREERS_WHY_START_SUBTITLE}
        titleType="title_md"
        subtitleType="headline_md"
        noCTA
      >
        <MotionBox
          variants={staggeredAppear}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <Box>
            {[
              trans.LBL_EARLY_CAREERS_WHY_START_BULLET_1,
              trans.LBL_EARLY_CAREERS_WHY_START_BULLET_2,
              trans.LBL_EARLY_CAREERS_WHY_START_BULLET_3,
              trans.LBL_EARLY_CAREERS_WHY_START_BULLET_5,
              trans.LBL_EARLY_CAREERS_WHY_START_BULLET_6,
            ].map((item, index) => (
              <MotionBox
                key={index}
                variants={viewAppearDefault}
                py={6}
                borderBottom="1px solid"
                borderColor="gray.600"
              >
                <Flex align="flex-start">
                  <Text type="p_lg" color="white" lineHeight="1.8">
                    {item}
                  </Text>
                </Flex>
              </MotionBox>
            ))}
          </Box>
        </MotionBox>
      </TextColumnSection>

      {/* Workplace Images */}
      <HorizontalSlideSection
        bg="black.900"
        baseSrc="/images/early-careers/gallery/"
        items={[
          "02",
          "13",
          "03-vert",
          "07",
          "05-vert",
          "06-vert",
          "04",
          "08-vert",
          "09",
          "10-vert",
          "11",
          "12-vert",
          "15",
          "16",
          "17",
          "14-vert",
        ]}
      />

      {/* Career Pathways Section */}
      <BgChangeSection
        animationId="access"
        initialColor="black.900"
        finalColor="gray.900"
      >
        <CareerPathwaysSection
          bg="transparent"
          title={trans.LBL_EARLY_CAREERS_PATHWAYS_TITLE}
          items={pathwayItems}
        />

        {/* Opportunities Section */}
        <PillsSection
          bg="transparent"
          title={trans.LBL_EARLY_CAREERS_OPPORTUNITIES_TITLE}
          subtitle={trans.LBL_EARLY_CAREERS_OPPORTUNITIES_SUBTITLE}
          subtitleType="headline_sm"
          pills={[
            trans.LBL_EARLY_CAREERS_OPPORTUNITIES_TEAM_1,
            trans.LBL_EARLY_CAREERS_OPPORTUNITIES_TEAM_2,
            trans.LBL_EARLY_CAREERS_OPPORTUNITIES_TEAM_3,
            trans.LBL_EARLY_CAREERS_OPPORTUNITIES_TEAM_4,
            trans.LBL_EARLY_CAREERS_OPPORTUNITIES_TEAM_5,
            trans.LBL_EARLY_CAREERS_OPPORTUNITIES_TEAM_6,
            trans.LBL_EARLY_CAREERS_OPPORTUNITIES_TEAM_7,
          ]}
        />
      </BgChangeSection>
      <BgChangeSection
        animationId="access2"
        initialColor="gray.900"
        finalColor="black.900"
      >
        {/* FAQ Section */}
        {faqCategories.map((cat, index) => (
          <FaqSection
            key={cat.slug}
            items={cat.faqs}
            title={cat.categoryTitle}
            bg="transparent"
            noSubtitle={index > 0}
          />
        ))}
        {/* Final CTA Section */}
        <Box h="2vh" bg="transparent" id="register" />
        <DarkQuoteSection
          title={trans.LBL_EARLY_CAREERS_FOOTER_CTA_TITLE}
          description={trans.LBL_EARLY_CAREERS_FOOTER_CTA_DESCRIPTION}
          bg="transparent"
        />
        <Box pb="20vh" bg="transparent" align="center">
          <Button
            w={["90%", "280px", "600px"]}
            href="https://hill-helicopters.applynow.net.au/jobs/HLH20-expressions-of-interest#form-tab-content"
            variant="solid"
          >
            {trans.LBL_EARLY_CAREERS_FORM_CTA}
          </Button>
        </Box>
      </BgChangeSection>
    </MainContainer>
  );
}

export async function getServerSideProps({ locale }) {
  const faqCategories = await getFaqsByPage("early-careers", locale);
  const allFaqs = faqCategories.flatMap((cat) => cat.faqs);

  const faqSchemaEntities = allFaqs
    .filter((faq) => faq.question && faq.answerText)
    .map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answerText,
      },
    }));

  const faqSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqSchemaEntities,
  });

  return {
    props: {
      faqCategories,
      faqSchema,
    },
  };
}
